import { useState } from "react";
import {
  ArrowsClockwise,
  CheckCircle,
  Lightning,
  Warning,
  XCircle,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { confirmProductionWrite } from "../lib/diagnostics";
import { toast } from "sonner";

/**
 * Iter 20.21 — DCG Pipeline V2 admin card.
 *
 * Single-operator, strict-gate, autonomous pipeline:
 *   POST /api/admin/pipeline/dcg-v2/dry-run
 *   POST /api/admin/pipeline/dcg-v2/apply
 *
 * Defaults per Message 607 + clarifications:
 *  - Auto-publish toggle OFF (admin clicks Apply each run).
 *  - Records that pass ALL gates → verified_live + pipeline_version="v2".
 *  - Previously-published v2 records no longer on the live listing →
 *    competition_data_status="expired" (always applies, even when
 *    Auto-publish is OFF).
 */
export default function DcgPipelineV2Card({ diag, onApplied }) {
  const [limit, setLimit] = useState(100);
  const [autoPublish, setAutoPublish] = useState(false);
  const [busy, setBusy] = useState(null);
  const [plan, setPlan] = useState(null);

  const runDryRun = async () => {
    setBusy("dry-run");
    setPlan(null);
    try {
      const { data } = await api.post(
        "/admin/pipeline/dcg-v2/dry-run", { limit },
      );
      setPlan(data);
      const s = data.summary || {};
      toast.success(
        `DCG V2 dry-run: ${s.cards_discovered || 0} cards · ` +
          `${s.eligible_to_publish || 0} pass all gates · ` +
          `${(data.rejected || []).length} rejected · ` +
          `${(data.expired || []).length} expiring.`,
      );
    } catch (e) {
      toast.error(
        "DCG V2 dry-run failed: " +
          (e.response?.data?.detail?.error ||
            JSON.stringify(e.response?.data?.detail) ||
            e.message),
      );
    } finally {
      setBusy(null);
    }
  };

  const runApply = async () => {
    if (!plan?.plan_id) return;
    const expiredCount = (plan.expired || []).length;
    const publishableCount = (plan.publishable || []).length;
    const writes = autoPublish ? publishableCount + expiredCount : expiredCount;
    if (writes === 0) {
      toast("Nothing to apply — no publishable or expired records.");
      return;
    }
    const summary = autoPublish
      ? `Auto-publish ${publishableCount} records to verified_live + ` +
        `expire ${expiredCount} stale records.`
      : `Expire ${expiredCount} stale records. ` +
        `${publishableCount} publishable records IGNORED (Auto-publish off).`;
    if (!confirmProductionWrite(diag, `Apply DCG V2 plan: ${summary}`)) return;
    setBusy("apply");
    try {
      const { data } = await api.post("/admin/pipeline/dcg-v2/apply", {
        plan_id: plan.plan_id,
        auto_publish: autoPublish,
      });
      toast.success(
        `DCG V2 applied: ${data.created} created · ${data.updated} updated · ` +
          `${data.expired} expired · ${(data.errors || []).length} errors.`,
      );
      setPlan({ ...plan, last_apply: data });
      if (onApplied) onApplied();
    } catch (e) {
      toast.error(
        "DCG V2 apply failed: " +
          (e.response?.data?.detail?.error ||
            JSON.stringify(e.response?.data?.detail) ||
            e.message),
      );
    } finally {
      setBusy(null);
    }
  };

  const summary = plan?.summary || null;
  const publishable = plan?.publishable || [];
  const rejected = plan?.rejected || [];
  const expired = plan?.expired || [];

  return (
    <div
      className="rounded-lg border border-emerald-500/40 bg-gradient-to-br from-emerald-500/[0.05] to-transparent p-5 space-y-4"
      data-testid="dcg-v2-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Lightning size={18} weight="bold" className="text-emerald-300" />
            <h3
              className="text-lg font-semibold text-white"
              data-testid="dcg-v2-title"
            >
              DCG Pipeline V2
            </h3>
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
              Autonomous · Dream Car Giveaways only
            </span>
          </div>
          <p className="text-xs text-white/60 max-w-2xl leading-relaxed">
            Fetches the live DCG listing, reads each detail page&apos;s
            structured competition object, and only publishes records that
            pass <strong>every gate</strong>: max_tickets present, ticket
            price &gt; 0, draw or closing date set, prize image found, and
            no closed signal. Stale previously-published records auto-expire.
            All non-DCG operators are paused.
          </p>
        </div>
        <div
          className="text-[10px] px-2 py-1 rounded border border-white/10 text-white/60"
          data-testid="dcg-v2-pipeline-flag"
        >
          pipeline_version=
          <span className="text-emerald-300">v2</span>
          {" · "}launch_pipeline=
          <span className="text-emerald-300">dcg_v2</span>
        </div>
      </div>

      {/* Controls */}
      <div className="grid sm:grid-cols-3 gap-3 text-xs">
        <label className="text-white/80">
          Limit:
          <input
            type="number"
            value={limit}
            min={1}
            max={300}
            onChange={(e) => setLimit(parseInt(e.target.value || "100", 10))}
            data-testid="dcg-v2-limit-input"
            className="ml-2 px-2 py-1 rounded bg-white/5 border border-white/10 text-xs w-20"
          />
        </label>
        <label
          className="inline-flex items-center gap-2 text-white/80"
          data-testid="dcg-v2-autopublish-label"
        >
          <input
            type="checkbox"
            checked={autoPublish}
            onChange={(e) => setAutoPublish(e.target.checked)}
            data-testid="dcg-v2-autopublish-toggle"
          />
          Auto-publish to <code className="text-[10px]">verified_live</code>{" "}
          <span
            className={
              autoPublish ? "text-emerald-300" : "text-amber-300/80"
            }
          >
            ({autoPublish ? "ON" : "OFF — default"})
          </span>
        </label>
        <div className="text-[10px] text-white/50 leading-snug">
          Auto-publish OFF still expires stale records on apply.
          Toggle ON to write new <code>verified_live</code> records.
        </div>
      </div>

      {/* Buttons */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={runDryRun}
          disabled={busy != null}
          data-testid="dcg-v2-dry-run-btn"
          className="btn-gold px-4 py-2 rounded text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          <ArrowsClockwise size={14} weight="bold" />
          {busy === "dry-run" ? "Running dry-run…" : "Dry-run DCG V2"}
        </button>
        <button
          onClick={runApply}
          disabled={busy != null || !plan?.plan_id}
          data-testid="dcg-v2-apply-btn"
          className="px-4 py-2 rounded text-xs inline-flex items-center gap-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <CheckCircle size={14} weight="bold" />
          {busy === "apply" ? "Applying…" : "Apply"}
        </button>
        {plan?.plan_id && (
          <span
            className="text-[10px] text-white/50 font-mono"
            data-testid="dcg-v2-plan-id"
          >
            plan {plan.plan_id.slice(0, 8)}
            {plan.plan_expires_at
              ? ` · expires ${new Date(plan.plan_expires_at).toLocaleTimeString(
                  "en-GB",
                )}`
              : ""}
          </span>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div
          className="rounded-md bg-black/30 border border-white/10 p-3 space-y-3"
          data-testid="dcg-v2-summary"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
            <Stat
              label="Cards discovered"
              value={summary.cards_discovered}
              testId="dcg-v2-cards-discovered"
            />
            <Stat
              label="Detail pages fetched"
              value={summary.detail_pages_fetched}
              testId="dcg-v2-detail-fetched"
              tone="muted"
            />
            <Stat
              label="Pass all gates"
              value={summary.eligible_to_publish}
              testId="dcg-v2-eligible"
              tone="ok"
            />
            <Stat
              label="Will be expired"
              value={summary.expired_existing}
              testId="dcg-v2-will-expire"
              tone={summary.expired_existing ? "warn" : "muted"}
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
            <Stat
              label="Rejected · closed"
              value={summary.rejected_closed}
              testId="dcg-v2-rej-closed"
              tone={summary.rejected_closed ? "warn" : "muted"}
            />
            <Stat
              label="Rejected · no max_tickets"
              value={summary.rejected_missing_max_tickets}
              testId="dcg-v2-rej-mt"
              tone={summary.rejected_missing_max_tickets ? "warn" : "muted"}
            />
            <Stat
              label="Rejected · no price"
              value={summary.rejected_missing_ticket_price}
              testId="dcg-v2-rej-price"
              tone={summary.rejected_missing_ticket_price ? "warn" : "muted"}
            />
            <Stat
              label="Rejected · no image"
              value={summary.rejected_missing_image}
              testId="dcg-v2-rej-img"
              tone={summary.rejected_missing_image ? "warn" : "muted"}
            />
          </div>
        </div>
      )}

      {/* Publishable list */}
      {publishable.length > 0 && (
        <details
          className="rounded-md bg-emerald-500/5 border border-emerald-500/30 p-3"
          data-testid="dcg-v2-publishable"
          open
        >
          <summary className="cursor-pointer text-xs font-semibold text-emerald-100">
            Publishable ({publishable.length})
          </summary>
          <div className="mt-2 max-h-72 overflow-auto space-y-1 text-[11px]">
            {publishable.slice(0, 100).map((p) => (
              <div
                key={p.detail_url}
                className="flex items-start justify-between gap-2 py-1 border-b border-white/5"
                data-testid={`dcg-v2-pub-row-${p.detail_url.slice(-30)}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-white/85 truncate font-medium">
                    {p.title}
                  </div>
                  <div className="text-white/50 truncate font-mono text-[10px]">
                    {p.detail_url}
                  </div>
                </div>
                <div className="text-[10px] text-white/60 shrink-0 text-right">
                  max=<strong className="text-emerald-300">
                    {(p.max_tickets || 0).toLocaleString("en-GB")}
                  </strong>
                  {" · "}
                  £{p.ticket_price}
                  {" · "}
                  score=<strong className="text-emerald-300">
                    {p.scores?.overall_compcheck_score}
                  </strong>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Rejected list */}
      {rejected.length > 0 && (
        <details
          className="rounded-md bg-amber-500/5 border border-amber-500/30 p-3"
          data-testid="dcg-v2-rejected"
        >
          <summary className="cursor-pointer text-xs font-semibold text-amber-100">
            Rejected ({rejected.length})
          </summary>
          <div className="mt-2 max-h-72 overflow-auto space-y-1 text-[10px]">
            {rejected.slice(0, 100).map((r, i) => (
              <div
                key={`${r.url}-${i}`}
                className="flex items-start gap-2 py-1 border-b border-white/5"
                data-testid={`dcg-v2-rej-row-${i}`}
              >
                <XCircle
                  size={12}
                  weight="bold"
                  className="text-amber-300 mt-0.5 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-white/80 truncate font-medium">
                    {r.title || r.url}
                  </div>
                  <div className="text-white/55">
                    <code className="text-[9px] text-amber-200">
                      {r.reason}
                    </code>
                    {r.evidence && (
                      <span className="text-white/40">
                        {" "}
                        · {String(r.evidence).slice(0, 140)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Expired list */}
      {expired.length > 0 && (
        <details
          className="rounded-md bg-red-500/5 border border-red-500/30 p-3"
          data-testid="dcg-v2-expired"
        >
          <summary className="cursor-pointer text-xs font-semibold text-red-100">
            Will be expired ({expired.length})
          </summary>
          <div className="mt-2 max-h-60 overflow-auto space-y-1 text-[10px]">
            {expired.slice(0, 100).map((e) => (
              <div
                key={e.id}
                className="flex items-start gap-2 py-1 border-b border-white/5"
                data-testid={`dcg-v2-exp-row-${e.id}`}
              >
                <Warning
                  size={12}
                  weight="bold"
                  className="text-red-300 mt-0.5 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-white/80 truncate font-medium">
                    {e.title || e.url}
                  </div>
                  <div className="text-white/50 text-[9px] truncate">
                    {e.url}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Last apply receipt */}
      {plan?.last_apply && (
        <div
          className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs text-emerald-100"
          data-testid="dcg-v2-last-apply"
        >
          Applied at{" "}
          {new Date(plan.last_apply.applied_at).toLocaleString("en-GB")}:{" "}
          <strong>{plan.last_apply.created}</strong> created ·{" "}
          <strong>{plan.last_apply.updated}</strong> updated ·{" "}
          <strong>{plan.last_apply.expired}</strong> expired ·{" "}
          {(plan.last_apply.errors || []).length} errors. Auto-publish was{" "}
          <strong>
            {plan.last_apply.auto_publish ? "ON" : "OFF"}
          </strong>
          .
        </div>
      )}

      {/* Listing URL footnote */}
      {plan?.listing_url && (
        <div className="text-[10px] text-white/40 font-mono break-all">
          Listing source: {plan.listing_url}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testId, tone }) {
  const toneCls =
    tone === "ok"
      ? "bg-emerald-500/15 text-emerald-200"
      : tone === "warn"
        ? "bg-amber-500/15 text-amber-200"
        : tone === "bad"
          ? "bg-red-500/15 text-red-200"
          : tone === "muted"
            ? "bg-white/5 text-white/60"
            : "bg-white/5 text-white/80";
  return (
    <div className={`rounded px-2 py-1.5 ${toneCls}`} data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wide opacity-70">
        {label}
      </div>
      <div className="text-sm font-semibold font-mono-num">{value ?? 0}</div>
    </div>
  );
}
